import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Typography, Popconfirm, Modal, Form, Input, Select, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';

const { Text } = Typography;

const ActivationCodes = () => {
  const [codes, setCodes] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await adminAPI.listActivationCodes();
      setCodes(response.data);
    } catch (error) {
      message.error('获取激活码列表失败');
    }
  };

  const handleRevoke = async (codeId) => {
    try {
      await adminAPI.revokeActivationCode(codeId);
      message.success('激活码已撤销');
      fetchCodes();
    } catch (error) {
      message.error('撤销失败');
    }
  };

  const handleCreate = async (values) => {
    setLoading(true);
    try {
      await adminAPI.createActivationCode(
        values.username,
        values.max_uses !== undefined ? values.max_uses : 1,
        values.expire_days !== undefined ? values.expire_days : 30
      );
      message.success('激活码创建成功');
      setCreateModalVisible(false);
      fetchCodes();
    } catch (error) {
      message.error(error.response?.data?.detail || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '激活码',
      dataIndex: 'activation_code',
      key: 'activation_code',
      width: 200,
      render: (code) => code ? (
        <Text copyable={{ text: code }} style={{ fontFamily: 'monospace' }}>
          {code}
        </Text>
      ) : '无法显示',
    },
    {
      title: '绑定用户',
      dataIndex: 'bound_username',
      key: 'bound_username',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const colorMap = {
          'ACTIVE': 'green',
          'CONSUMED': 'orange',
          'EXPIRED': 'red',
          'REVOKED': 'default'
        };
        return <Text type={colorMap[status] || 'default'}>{status}</Text>;
      }
    },
    {
      title: '使用次数',
      key: 'usage',
      width: 120,
      render: (_, record) => `${record.used_count}/${record.display_max_uses || record.max_uses}`,
    },
    {
      title: '有效期',
      key: 'expires',
      width: 150,
      render: (_, record) => {
        if (record.display_expires) {
          return <Text type="secondary">{record.display_expires}</Text>;
        }
        return <Text type="success">永久有效</Text>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        record.status === 'ACTIVE' && (
          <Popconfirm
            title="确定要撤销此激活码吗？"
            onConfirm={() => handleRevoke(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" danger>撤销</Button>
          </Popconfirm>
        )
      )
    }
  ];

  return (
    <Card
      title="激活码管理"
      extra={
        <Button type="primary" onClick={() => setCreateModalVisible(true)}>
          生成激活码
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={codes}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1000 }}
      />

      <Modal
        title="生成激活码"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="username"
            label="绑定用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            name="max_uses"
            label="最大使用次数"
            initialValue={1}
          >
            <Select placeholder="请选择使用次数">
              <Select.Option value={1}>1次（一次性）</Select.Option>
              <Select.Option value={5}>5次</Select.Option>
              <Select.Option value={10}>10次</Select.Option>
              <Select.Option value={0}>无限次数</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="expire_days"
            label="有效期"
            initialValue={30}
          >
            <Select placeholder="请选择有效期">
              <Select.Option value={7}>7天</Select.Option>
              <Select.Option value={30}>30天</Select.Option>
              <Select.Option value={90}>90天</Select.Option>
              <Select.Option value={365}>1年</Select.Option>
              <Select.Option value={-1}>永久有效</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading} block>
              生成
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ActivationCodes;
